import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

/*
  A form in a sheet.

  Settings was three password fields, a confirm field and an invite field, all of them
  empty, all of them permanently on screen: about a thousand pixels spent on four boxes
  nobody was filling in. The facts an owner came to read were pushed off the bottom.

  A form that is used once a year does not deserve standing room. Everything on Settings is
  now a row that states what is currently true, and the form that changes it opens here.

  The same .sheet-backdrop and .sheet the capture sheet and the seat picker use: bottom
  sheet on a phone, centred panel on a desktop. Portalled to the body because a sheet
  rendered inside a card inherits its stacking context and opens half off the screen.
*/
export function Sheet(
  { title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode },
) {
  const panel = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', key)

    // Straight into the first field. Somebody who has just pressed "Change" has said what
    // they want and should not have to click again to start typing.
    panel.current?.querySelector<HTMLInputElement>('input, textarea')?.focus()

    return () => document.removeEventListener('keydown', key)
  }, [onClose])

  return createPortal(
    <div className="sheet-backdrop" onClick={onClose}>
      <div
        ref={panel}
        className="sheet sheet--form"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={e => e.stopPropagation()}
      >
        <div className="sheet__head">
          <h2 className="sheet__title">{title}</h2>
          <button className="sheet__close" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  )
}
